export type ThemePreference = 'system' | 'light' | 'dark';
type ResolvedTheme = 'light' | 'dark';

const THEME_STORAGE_KEY = 'exhibit_theme_preference';
const THEME_EVENT = 'exhibit-theme-change';

function isThemePreference(value: string | null): value is ThemePreference {
  return value === 'system' || value === 'light' || value === 'dark';
}

export function getThemePreference(): ThemePreference {
  if (typeof window === 'undefined') return 'system';
  const stored = window.localStorage.getItem(THEME_STORAGE_KEY);
  return isThemePreference(stored) ? stored : 'system';
}

export function getResolvedTheme(preference: ThemePreference): ResolvedTheme {
  if (preference === 'light' || preference === 'dark') return preference;
  if (typeof window === 'undefined') return 'light';
  return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
}

export function applyTheme(preference: ThemePreference): void {
  if (typeof document === 'undefined') return;
  const resolved = getResolvedTheme(preference);
  document.documentElement.dataset.theme = resolved;
  document.documentElement.style.colorScheme = resolved;
}

export function setThemePreference(preference: ThemePreference): void {
  if (typeof window === 'undefined') return;
  window.localStorage.setItem(THEME_STORAGE_KEY, preference);
  applyTheme(preference);
  window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: preference }));
}

let initialized = false;

export function initTheme(): void {
  if (initialized || typeof window === 'undefined') return;
  initialized = true;

  applyTheme(getThemePreference());

  const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
  const handleSystemThemeChange = () => {
    if (getThemePreference() === 'system') {
      applyTheme('system');
      window.dispatchEvent(new CustomEvent(THEME_EVENT, { detail: 'system' satisfies ThemePreference }));
    }
  };

  mediaQuery.addEventListener('change', handleSystemThemeChange);
}

export function subscribeToThemePreference(
  callback: (preference: ThemePreference) => void,
): () => void {
  if (typeof window === 'undefined') return () => {};

  const handler = (event: Event) => {
    const customEvent = event as CustomEvent<ThemePreference>;
    callback(customEvent.detail ?? getThemePreference());
  };

  const storageHandler = (event: StorageEvent) => {
    if (event.key === THEME_STORAGE_KEY) {
      callback(getThemePreference());
    }
  };

  window.addEventListener(THEME_EVENT, handler);
  window.addEventListener('storage', storageHandler);

  return () => {
    window.removeEventListener(THEME_EVENT, handler);
    window.removeEventListener('storage', storageHandler);
  };
}
